// src/beta/store/reader.ts
import { create } from 'zustand';

export type ReaderDirection = 'forward' | 'backward';

interface ReaderState {
  storyId: string | null;
  slideIndex: number;
  direction: ReaderDirection;

  open: (storyId: string) => void;
  close: () => void;
  next: (slideCount: number) => void;
  prev: () => void;
  setSlideIndex: (index: number) => void;
  reverseDirection: () => void;
  reset: () => void;
}

const DEFAULTS = {
  storyId: null as string | null,
  slideIndex: 0,
  direction: 'forward' as ReaderDirection,
};

/** UI state for the StoryReader and AttractLoop.
 *  - `storyId`: which story is being read (null when reader is closed)
 *  - `slideIndex`: 0-based index into the story's slide list
 *  - `direction`: forward/backward — drives crossfade ordering
 *
 *  Direction is also used by the attract loop so a wake-back returns to the
 *  prior axis with the loop reversed (audience never sees the same opening
 *  twice in a row). See spec § 6.5. */
export const useReaderStore = create<ReaderState>((set, get) => ({
  ...DEFAULTS,

  open: (storyId) => set({ storyId, slideIndex: 0 }),

  close: () => set({ ...DEFAULTS }),

  next: (slideCount) => {
    const i = get().slideIndex;
    if (i >= slideCount - 1) return;
    set({ slideIndex: i + 1, direction: 'forward' });
  },

  prev: () => {
    const i = get().slideIndex;
    if (i <= 0) return;
    set({ slideIndex: i - 1, direction: 'backward' });
  },

  setSlideIndex: (slideIndex) => set({ slideIndex }),

  reverseDirection: () =>
    set({ direction: get().direction === 'forward' ? 'backward' : 'forward' }),

  reset: () => set({ ...DEFAULTS }),
}));
